import { Link, useParams } from "react-router-dom";

import { Layout } from "@/components/layout";
import { usePost } from "@/lib/queries/use-posts";

function formatFullDate(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// Check if content is HTML (from WYSIWYG editor) or plain text/markdown
function isHtmlContent(content: string): boolean {
  // Check for common HTML tags that the WYSIWYG editor produces
  return /<[a-z][\s\S]*>/i.test(content);
}

// Render HTML content from WYSIWYG editor
function renderHtmlContent(content: string) {
  return <div className="prose-content" dangerouslySetInnerHTML={{ __html: content }} />;
}

// Simple markdown-like rendering for legacy content
function renderMarkdownContent(content: string) {
  // Split by double newlines to create paragraphs
  const paragraphs = content.split(/\n\n+/);

  return paragraphs.map((paragraph, index) => {
    const trimmed = paragraph.trim();

    // Headers
    if (trimmed.startsWith("### ")) {
      return (
        <h3 key={index} className="text-[20px] font-bold text-primary mt-10 mb-4">
          {trimmed.slice(4)}
        </h3>
      );
    }
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={index} className="text-[24px] font-bold text-primary mt-12 mb-4">
          {trimmed.slice(3)}
        </h2>
      );
    }

    // Code blocks
    if (trimmed.startsWith("```") && trimmed.endsWith("```")) {
      const code = trimmed.slice(3, -3).replace(/^\w+\n/, "");
      return (
        <pre
          key={index}
          className="bg-card border border-border p-4 overflow-x-auto my-6 text-[14px] text-text-secondary"
        >
          <code>{code}</code>
        </pre>
      );
    }

    // Blockquotes
    if (trimmed.startsWith("> ")) {
      return (
        <blockquote
          key={index}
          className="border-l-2 border-text-faint pl-4 my-6 text-text-tertiary italic"
        >
          {trimmed.slice(2)}
        </blockquote>
      );
    }

    // Regular paragraphs
    // Handle inline formatting
    const formattedText = trimmed
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-primary">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Inline code
      .replace(/`(.*?)`/g, '<code class="bg-card px-1.5 py-0.5 text-[13px]">$1</code>')
      // Links
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" class="text-primary underline underline-offset-2 hover:text-text-secondary" target="_blank" rel="noopener noreferrer">$1</a>'
      );

    return (
      <p
        key={index}
        className="text-[16px] text-text-secondary leading-[1.8] mb-6"
        dangerouslySetInnerHTML={{ __html: formattedText }}
      />
    );
  });
}

// Render content - automatically detect HTML vs markdown
function renderContent(content: string) {
  if (isHtmlContent(content)) {
    return renderHtmlContent(content);
  }
  return renderMarkdownContent(content);
}

export function WritingPost() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, error } = usePost(slug || "");

  if (isLoading) {
    return (
      <Layout>
        <section className="mx-auto max-w-3xl px-8 pt-20 pb-16">
          <p className="text-text-muted">Loading...</p>
        </section>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <section className="mx-auto max-w-3xl px-8 pt-20 pb-16">
          <Link
            to="/writing"
            className="text-[12px] text-text-muted hover:text-primary tracking-wider uppercase mb-8 inline-block"
          >
            ← Back to Writing
          </Link>
          <h1 className="text-[32px] font-bold text-primary mb-4">Post Not Found</h1>
          <p className="text-text-secondary">
            The post you're looking for doesn't exist or has been removed.
          </p>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-8 pt-12 pb-20">
        {/* Back link */}
        <Link
          to="/writing"
          className="text-[12px] text-text-muted hover:text-primary tracking-wider uppercase mb-12 inline-block"
        >
          ← Back to Writing
        </Link>

        {/* Header */}
        <header className="mb-12 border-b border-border pb-8">
          <div className="flex items-center gap-3 mb-4">
            {post.type === "note" && (
              <span className="text-[10px] text-text-faint tracking-caps uppercase px-2 py-0.5 border border-border">
                Note
              </span>
            )}
            <time className="text-[12px] text-text-muted tracking-wider">
              {formatFullDate(post.published_at)}
            </time>
          </div>
          <h1 className="text-[36px] font-bold text-primary leading-[1.2] tracking-tight mb-4">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-[17px] text-text-tertiary leading-relaxed">{post.excerpt}</p>
          )}
        </header>

        {/* Content */}
        <div className="prose-custom">{renderContent(post.content)}</div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border">
          <Link
            to="/writing"
            className="text-[13px] text-text-muted hover:text-primary tracking-wider"
          >
            ← More writing
          </Link>
        </footer>
      </article>
    </Layout>
  );
}
